import { SkinAnalyse } from "../../type"

type DemkyConcernLevel = "high" | "medium" | "low" | "very_low"

const levelToScore = (level?: DemkyConcernLevel | string | number): number => {
    if (typeof level === "number") {
        return clampScore(level)
    }

    const numericLevel = Number(level)
    if (!Number.isNaN(numericLevel)) {
        return clampScore(numericLevel)
    }

    switch (level) {
        case "high":
            return 1
        case "medium":
            return 0.66
        case "low":
            return 0.33
        case "very_low":
            return 0.15
        default:
            return 0
    }
}

const clampScore = (score: number): number => {
    return Math.max(0, Math.min(score, 1))
}

const getConcernScore = (demkyResult: any, concern: string, missingValue = 0): number => {
    const result = demkyResult?.results?.[concern]
    const level = result?.level ?? result?.correlated_level

    if (level === undefined || level === null || level === "") {
        console.warn(`[Demky] Missing concern in response: ${concern}`)
        return missingValue
    }

    return levelToScore(level)
}

const hasResults = (demkyResult: any): boolean => {
    if (!demkyResult?.results) {
        console.error("[Demky] No results in response", demkyResult)
        return false
    }

    return true
}

export const demkyParser = (demkyResult: any): SkinAnalyse[] => {
    if (!hasResults(demkyResult)) {
        return [
            { name: "irritation", score: -1 },
            { name: "blemishes", score: -1 },
            { name: "fatigue", score: -1 },
            { name: "lines_wrinkles", score: -1 },
            { name: "uneven_skintone", score: -1 },
            { name: "pores_uneven_structure", score: -1 },
            { name: "loss_of_volume", score: -1 },
            { name: "dehydration", score: -1 },
        ]
    }

    const skinAnalysis: SkinAnalyse[] = [
        { name: "irritation", score: computeIrritation(demkyResult) },
        { name: "blemishes", score: getConcernScore(demkyResult, "ACNE", -1) },
        { name: "fatigue", score: computeFatigue(demkyResult) },
        { name: "lines_wrinkles", score: getConcernScore(demkyResult, "WRINKLES", -1) },
        { name: "uneven_skintone", score: computeSkinTone(demkyResult) },
        { name: "pores_uneven_structure", score: computePoresAndTexture(demkyResult) },
        { name: "loss_of_volume", score: computeSagging(demkyResult) },
        { name: "dehydration", score: computeDehydration(demkyResult) },
    ]

    console.warn(skinAnalysis)
    return skinAnalysis.sort((a: SkinAnalyse, b: SkinAnalyse) => {
        return b.score - a.score;
    });
}

const computeFatigue = (demkyResult: any): number => {
    const darkCirclesScore = getConcernScore(demkyResult, "DARK_CIRCLES")
    const eyeBagsScore = getConcernScore(demkyResult, "EYE_BAGS")

    return (eyeBagsScore + darkCirclesScore) / 2
}

const computeIrritation = (demkyResult: any): number => {
    const acneScore = getConcernScore(demkyResult, "ACNE")
    const rednessScore = getConcernScore(demkyResult, "REDNESS")

    return (rednessScore + acneScore) / 2
}

const computePoresAndTexture = (demkyResult: any): number => {
    const poresScore = Math.max(
        getConcernScore(demkyResult, "PORES_LEFT_CHEEK"),
        getConcernScore(demkyResult, "PORES_RIGHT_CHEEK"),
        getConcernScore(demkyResult, "PORES_T_ZONE")
    )

    const roughnessScore = Math.max(
        getConcernScore(demkyResult, "ROUGHNESS_CHIN"),
        getConcernScore(demkyResult, "ROUGHNESS_FOREHEAD"),
        getConcernScore(demkyResult, "ROUGHNESS_LEFT_CHEEK"),
        getConcernScore(demkyResult, "ROUGHNESS_RIGHT_CHEEK"),
        getConcernScore(demkyResult, "ROUGHNESS_NOSE")
    )

    return (roughnessScore + poresScore) / 2
}

const computeSkinTone = (demkyResult: any): number => {
    const heterogeneityScore = getConcernScore(demkyResult, "HETEROGENEITY")
    const darkSpotsScore = Math.max(
        getConcernScore(demkyResult, "DARK_SPOTS_FOREHEAD"),
        getConcernScore(demkyResult, "DARK_SPOTS_LEFT_CHEEK"),
        getConcernScore(demkyResult, "DARK_SPOTS_RIGHT_CHEEK"),
        getConcernScore(demkyResult, "DARK_SPOTS_LOWER_FACE"),
        getConcernScore(demkyResult, "DARK_SPOTS_NOSE")
    )

    return (heterogeneityScore + darkSpotsScore) / 2
}

const computeSagging = (demkyResult: any): number => {
    return Math.max(
        getConcernScore(demkyResult, "HOLLOW_CHEEKS_LEFT"),
        getConcernScore(demkyResult, "HOLLOW_CHEEKS_RIGHT")
    )
}

const computeDehydration = (demkyResult: any): number => {
    const dehydrationScore = getConcernScore(demkyResult, "DEHYDRATION", -1)
    if (dehydrationScore < 0) {
        return -1
    }

    return dehydrationScore > 0.3 ? 1 : 0
}
